// Imports
import { Pagination, PostOrPage } from '@tryghost/content-api';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useSearchParams } from 'react-router-dom';
import styled from 'styled-components/macro';
import commonActions from '../../../shareables/foundation/front-end/redux/actions';
import { useAppDispatch } from '../../../shareables/foundation/front-end/redux/hooks';
import LoadingImage from '../../resources/loading-image';
import SimplePageTemplate from '../../resources/simple-page-template';
import ghost from '../../../shareables/integrations/ghost';
import Previews from './previews';
import Toggle from './toggle';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import convertElementPlainQuotes from '../../../utils/convert-element-plain-quotes';
import useUpdateOnlyEffect from '../../../shareables/foundation/front-end/utils/use-update-only-effect';
import prerenderData from '../../../utils/prerender-data';


// The key identifying the latest post within the prerendered snapshot
const prerenderKey = 'blog:latest';


// Map category to tags
export const categoryMapping = {
	ALL: null,
	PRODUCT: ['feature-spotlight', 'website-tips', 'recap'],
	NEWS: ['meet-the-team', 'announcements'],
	COMMUNITY: ['community-tips'],
};


// Styled components
const RecentPost = styled(Link)`
	margin-top: 3.75rem;
	display: flex;
	text-decoration: none;
	align-items: center;
	gap: 1.25rem;
	width: min(56.25rem, 95%);
	
	@media (max-width: 48rem) {
		flex-direction: column;
	}
`;

const RecentFeatureImage = styled(LoadingImage)`
	width: min(30.625rem, 100%);
	flex-shrink: 0;
	aspect-ratio: 2 / 1;
	border-radius: 0.625rem;
	object-fit: cover;
`;

const Tags = styled.h4`
	color: hsl(206, 69%, 35%);
	font-size: 0.875rem;
	font-weight: 400;
`;

const MetaSkeletonWrapper = styled.div`
	width: min(18.75rem, 95%);
`;

const Title = styled.h3`
	color: hsl(206, 25%, 20%);
	font-size: 1.5rem;
	font-weight: 600;
	line-height: 1.2;
	margin-top: 0.375rem;
	margin-bottom: 0.625rem;
`;

const RecentMetadata = styled.div`
	@media (max-width: 48rem) {
		max-width: 30.625rem;
	}
	
	width: min(24.375rem, 100%);
`;

const Excerpt = styled.div`
	display: -webkit-box;
	overflow-y: hidden;
	-webkit-line-clamp: 4;
	-webkit-box-orient: vertical;
	overflow-wrap: anywhere;
	
	color: hsl(206, 25%, 20%, 1);
	font-size: 1.125rem !important;
	font-weight: 400;
	line-height: 1.2 !important;
	margin-bottom: 0 !important;
	width: min(30.625rem, 100%);
`;

const Authors = styled.div`
	display: flex;
	margin-top: 0.625rem;
	margin-right: auto;
	gap: 0.375rem;
	align-items: center;
`;

const AuthorImageContainer = styled.div`
	display: flex;
	margin-left: 0.375rem;
	margin-right: -0.125rem;
	margin-top: -0.125rem;
	margin-bottom: -0.125rem;
	height: 2.25rem;
`;

const AuthorNamePublishedContainer = styled.div`
	width: 100%;
`;

const AuthorName = styled.div`
	font-size: 0.875rem !important;
	margin-bottom: 0.25rem !important;
	color: hsl(206, 25%, 20%);
	line-height: 1 !important;
`;

const PublishedDate = styled.div`
	font-size: 0.75rem !important;
	color: hsl(206, 15%, 40%);
	line-height: 1 !important;
`;

const ProfileImage = styled(LoadingImage)`
	border: 0.125rem solid hsl(0, 0%, 100%);
	border-radius: 62.4375rem;
	width: 2.25rem;
	height: 2.25rem;
	margin-left: -0.5rem;
`;

const ProfileImageSkeleton = styled(Skeleton)`
	margin-left: -0.5rem;
`;


// Define the accepted props
interface Props {
	storybookLatestPost?: PostOrPage;
	storybookPosts?: PostOrPage[];
	storybookPagination?: Pagination;
}


// Function component
const Blog: React.FC<Props> = ({ storybookLatestPost, storybookPosts, storybookPagination }) => {
	// Use state
	const [latestPost, setLatestPost] = useState<PostOrPage | undefined>(
		storybookLatestPost ?? prerenderData.read<PostOrPage>(prerenderKey)
	);
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	
	
	// Use React Router functionality
	const [searchParams, setSearchParams] = useSearchParams();
	const urlView = searchParams.get('view')?.toUpperCase();
	
	
	// Use state
	const [view, setView] = useState<keyof typeof categoryMapping>(
		urlView && urlView in categoryMapping ? (urlView as keyof typeof categoryMapping) : 'ALL'
	);
	
	
	// Use ref
	const postRef = useRef<HTMLAnchorElement | null>(null);
	const initialPageRef = useRef(parseInt(searchParams.get('page') ?? '1'));
	
	
	// Handle error
	const handleError = useCallback(
		() =>
			dispatch(
				commonActions.showPageMessage({
					color: 'danger',
					title: 'Oh snap!',
					message: 'There was an unexpected issue.',
				})
			),
		[dispatch]
	);
	
	
	// Load first post on mount
	useEffect(() => {
		// Do not fetch in Storybook
		if ((window as typeof window & { STORYBOOK?: true }).STORYBOOK) {
			return;
		}
		
		
		// Restore the prerendered post then revalidate in the background
		return prerenderData.revalidate<PostOrPage>(
			prerenderKey,
			setLatestPost,
			() => ghost.posts.browse({ limit: 1, include: ['authors', 'tags'] }).then((response) => response[0]),
			() => {
				// Stay quiet while prerendering; an error notification shouldn't end up in the captured HTML
				if (!window.IS_PRERENDERING) {
					handleError();
				}
			}
		);
	}, [handleError]);
	
	
	// Insert smart quotes
	useEffect(() => {
		if (postRef.current) {
			convertElementPlainQuotes(postRef.current);
		}
	}, [latestPost]);
	
	
	// Update URL params when view toggle changes
	useUpdateOnlyEffect(() => {
		const params = new URLSearchParams(searchParams);
		
		if (view === 'ALL') {
			params.delete('view');
		} else {
			params.set('view', view.toLowerCase());
		}
		
		params.delete('page');
		
		setSearchParams(params);
	}, [view]);
	
	
	// Initialize
	const hasCategoryView = searchParams.has('view');
	
	
	// Return JSX
	return (
		<SimplePageTemplate
			heading='Blog articles'
			descriptor='Announcements, tips, and best practices for community leaders'
		>
			{hasCategoryView && (
				<Helmet>
					<meta name='robots' content='noindex, nofollow' />
				</Helmet>
			)}
			
			<RecentPost ref={postRef} to={latestPost ? latestPost.slug : '.'}>
				<RecentFeatureImage
					src={latestPost?.feature_image ?? ''}
					alt={latestPost?.feature_image_alt ?? latestPost?.title ?? ''}
				/>
				<RecentMetadata>
					<Tags>
						{latestPost ? (
							latestPost.tags?.map((tag) => tag.name).join(', ')
						) : (
							<MetaSkeletonWrapper>
								<Skeleton width='5rem' />
							</MetaSkeletonWrapper>
						)}
					</Tags>
					<Title>{latestPost ? latestPost.title : <Skeleton width='100%' />}</Title>
					<div>
						<Excerpt>
							{latestPost ? (
								latestPost.excerpt?.split('\n\n')[0].replace(/\n/g, ' ').trim()
							) : (
								<Skeleton count={4} width='100%' />
							)}
						</Excerpt>
					</div>
					<Authors>
						<AuthorImageContainer>
							{latestPost ? (
								latestPost.authors?.map((author) => (
									<ProfileImage key={author.id} src={author.profile_image ?? ''} alt={author.name} />
								))
							) : (
								<ProfileImageSkeleton width='2.25rem' height='2.25rem' borderRadius='62.4375rem' />
							)}
						</AuthorImageContainer>
						<AuthorNamePublishedContainer>
							<AuthorName>
								{latestPost ? (
									latestPost.authors?.map((author) => author.name).join(', ')
								) : (
									<MetaSkeletonWrapper>
										<Skeleton width='4rem' />
									</MetaSkeletonWrapper>
								)}
							</AuthorName>
							<PublishedDate>
								{latestPost?.published_at ? (
									new Date(latestPost.published_at).toLocaleDateString(undefined, {
										month: 'long',
										day: 'numeric',
										year: 'numeric',
									})
								) : (
									<MetaSkeletonWrapper>
										<Skeleton width='5rem' />
									</MetaSkeletonWrapper>
								)}
							</PublishedDate>
						</AuthorNamePublishedContainer>
					</Authors>
				</RecentMetadata>
			</RecentPost>
			
			<Toggle view={view} setView={setView} />
			
			<Previews
				postIDFilter={latestPost?.id}
				preventLoad={!latestPost?.id}
				handleError={handleError}
				tagFilter={categoryMapping[view]}
				storybookPosts={storybookPosts}
				storybookPagination={storybookPagination}
				initialPage={initialPageRef.current}
				resetKey={view}
			/>
		</SimplePageTemplate>
	);
};

export default Blog;
